import { FormEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Field,
  Fieldset,
  Heading,
  Input,
  Label,
  Radio,
  Select,
  Textarea,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { FactorDimension, TemplateFactorInput } from '../../../../api/hms';
import { CONSEQUENCE_LEVELS, consequenceOption, FACTOR_DIMENSIONS } from '../../hmsFormat';
import layout from '../../hmsPage.module.css';
import classes from '../HRRiskFactors.module.css';

type Props = {
  saving: boolean;
  onSubmit: (factor: TemplateFactorInput) => void;
  onCancel: () => void;
};

export const NewFactorForm = ({ saving, onSubmit, onCancel }: Props): ReactElement => {
  const { t } = useTranslation();
  const [factor, setFactor] = useState({
    name: '',
    description: '',
    relevanceReason: '',
    consequence: 3,
    dimension: 'LOAD' as FactorDimension,
  });
  const [tried, setTried] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const nameError = tried && !factor.name.trim();
  const reasonError = tried && !factor.relevanceReason.trim();

  useEffect(() => nameRef.current?.focus(), []);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!factor.name.trim() || !factor.relevanceReason.trim()) {
      setTried(true);
      (factor.name.trim() ? reasonRef : nameRef).current?.focus();
      return;
    }
    onSubmit({
      name: factor.name.trim(),
      description: factor.description.trim(),
      relevanceReason: factor.relevanceReason.trim(),
      consequence: factor.consequence,
      dimension: factor.dimension,
      questions: [],
    });
  };

  return (
    <form onSubmit={submit} noValidate className={classes.newFactor}>
      <Heading level={3} data-size="xs">
        {t('hrRiskFactors.newFactor.title')}
      </Heading>
      <Field data-size="sm">
        <Label htmlFor="hr-rf-new-name">{t('hrRiskFactors.newFactor.name')}</Label>
        <Input
          ref={nameRef}
          id="hr-rf-new-name"
          aria-invalid={nameError || undefined}
          value={factor.name}
          onChange={(event) => setFactor({ ...factor, name: event.target.value })}
        />
        {nameError && <ValidationMessage>{t('hrRiskFactors.newFactor.nameError')}</ValidationMessage>}
      </Field>
      <Field data-size="sm">
        <Label htmlFor="hr-rf-new-description">{t('hrRiskFactors.newFactor.description')}</Label>
        <Field.Description id="hr-rf-new-description-help">{t('hrRiskFactors.newFactor.descriptionHelp')}</Field.Description>
        <Textarea
          id="hr-rf-new-description"
          rows={2}
          aria-describedby="hr-rf-new-description-help"
          value={factor.description}
          onChange={(event) => setFactor({ ...factor, description: event.target.value })}
        />
      </Field>
      <Field data-size="sm">
        <Label htmlFor="hr-rf-new-why">{t('hrRiskFactors.newFactor.relevance')}</Label>
        <Field.Description id="hr-rf-new-why-help">{t('hrRiskFactors.newFactor.relevanceHelp')}</Field.Description>
        <Textarea
          ref={reasonRef}
          id="hr-rf-new-why"
          rows={2}
          aria-describedby="hr-rf-new-why-help"
          aria-invalid={reasonError || undefined}
          value={factor.relevanceReason}
          onChange={(event) => setFactor({ ...factor, relevanceReason: event.target.value })}
        />
        {reasonError && <ValidationMessage>{t('hrRiskFactors.newFactor.relevanceError')}</ValidationMessage>}
      </Field>
      <Fieldset data-size="sm">
        <Fieldset.Legend>{t('hrRiskFactors.newFactor.dimension')}</Fieldset.Legend>
        <Fieldset.Description>{t('hrRiskFactors.newFactor.dimensionHelp')}</Fieldset.Description>
        {FACTOR_DIMENSIONS.map((dimension) => (
          <Radio
            key={dimension}
            name="hr-rf-new-dimension"
            value={dimension}
            label={t(`hms.dimensions.${dimension}`)}
            checked={factor.dimension === dimension}
            onChange={() => setFactor({ ...factor, dimension })}
          />
        ))}
      </Fieldset>
      <Field data-size="sm" className={classes.consequenceField}>
        <Label htmlFor="hr-rf-new-consequence">{t('hrRiskFactors.newFactor.consequence')}</Label>
        <Field.Description id="hr-rf-new-consequence-help">{t('hrRiskFactors.newFactor.consequenceHelp')}</Field.Description>
        <Select
          id="hr-rf-new-consequence"
          aria-describedby="hr-rf-new-consequence-help"
          value={factor.consequence}
          onChange={(event) => setFactor({ ...factor, consequence: Number(event.target.value) })}
        >
          {CONSEQUENCE_LEVELS.map((level) => (
            <Select.Option key={level} value={level}>
              {consequenceOption(level, t)}
            </Select.Option>
          ))}
        </Select>
      </Field>
      <div className={layout.actions} data-gap="sm">
        <Button type="submit" variant="primary" data-size="sm" loading={saving}>
          {t('hrRiskFactors.newFactor.submit')}
        </Button>
        <Button type="button" variant="secondary" data-size="sm" onClick={onCancel}>
          {t('hrRiskFactors.cancel')}
        </Button>
      </div>
    </form>
  );
};
